/* KLIK floating WhatsApp contact (v2 · bugs-nav, K-107). Native link, no widget, no requests.
   The bubble is hidden (visibility:hidden via .is-hidden + inert, never opacity only) for any of these reasons:
     page      /cart (all sizes); order tracking and Projects on phones (they carry their own contact)
     dialog    a dialog, sheet, drawer or studio is open
     typing    a text field has focus on a phone (the keyboard is up)
     hero      the home hero's buttons are on screen
     footer    the footer is on screen (it has its own WhatsApp link in the contact row; the bubble never sits on the
               footer's accordion toggles, links or payment row)
     collide   it would cover a price or a call to action (IntersectionObserver with the viewport cropped to the bubble).
               It hides at once, but comes back only when nothing has been under it for 700 ms AND scrolling has
               stopped, so a grid of prices passing under it does not make it blink.
     tucked    phones, after a sustained scroll down (120 px); it returns on scroll up or after a short pause
   Calm: the bubble never re-appears more than once a second.
   On the custom-quote product page klik-product-dock.js may also set the `hidden` attribute while its sticky bar owns
   the bottom edge; that stays its decision. No page-wide MutationObserver, no layout reads while scrolling.
   wave1 pdp SP-02 (2026-10-27): on the product pages run by klik-pdp.js the old dock no longer runs (it measured the page
   on every scroll frame for a bar that never showed), so this script now knows every studio state the dock used to read:
   the root classes of the sofa, corner, room, furniture and ready studios, the product-options popup, a started studio
   root, a photo viewer and full screen. */
(() => {
  'use strict';
  const bubble = document.getElementById('KlikWhatsApp');
  if (!bubble || bubble.dataset.bound) return;
  bubble.dataset.bound = 'true';
  const html = document.documentElement, body = document.body;
  const phone = matchMedia('(max-width: 749px)');
  const reasons = new Set();
  let frame = 0, rebuildTimer = 0, collideObserver = null;
  let visible = true, lastShow = -Infinity, showTimer = 0;
  const now = () => performance.now();

  function apply() {
    frame = 0;
    const hide = reasons.size > 0;
    if (!hide && !visible) {
      const wait = 1000 - (now() - lastShow);
      if (wait > 0) {
        clearTimeout(showTimer);
        showTimer = setTimeout(() => { if (!frame) frame = requestAnimationFrame(apply); }, wait);
        return;
      }
      lastShow = now();
    }
    visible = !hide;
    bubble.classList.toggle('is-hidden', hide);
    bubble.classList.toggle('klik-whatsapp--tucked', reasons.has('tucked') && reasons.size === 1);
    if (bubble.inert !== hide) bubble.inert = hide;
    bubble.dataset.hiddenBy = [...reasons].join(' ');
  }
  function set(reason, on) {
    if (on === reasons.has(reason)) return;
    on ? reasons.add(reason) : reasons.delete(reason);
    if (!frame) frame = requestAnimationFrame(apply);
    if (reason === 'tucked' && !on && !collideObserver) rebuild();
  }

  /* page */
  const pageBlocked = () => body.classList.contains('template-cart') ||
    (phone.matches && !!document.querySelector('[data-koj-root],.kp-v2'));
  set('page', pageBlocked());
  phone.addEventListener?.('change', () => set('page', pageBlocked()));

  /* dialog: checked after the interactions that open or close one, and when the root classes change */
  const openSelector = 'dialog[open],.modal--is-active,.drawer--is-open,[role=dialog][aria-modal=true]:not([hidden])';
  const studioClasses = /(^|\s)(js-drawer-open|js-modal-open|kf-studio-open|ready-stock-open|hz-studio-open|hz-studio-active|kd-studio-open|kr-studio-open|klik-studio-open|klik-dpo-builder-active|sofa-fixed-open|modal-open)(\s|$)/;
  // shown while one of these is on screen (wave1 pdp: the states klik-product-dock.js read)
  const studioOpenSelector = '.pswp--open,#itoris_dynamicproductoptions_popup,[data-klik-dpo-wizard].is-builder-started,dialog.kd-opening[open]';
  const rendered = node => !node.closest('[hidden],[inert],[aria-hidden="true"]') && node.getClientRects().length > 0 && getComputedStyle(node).visibility !== 'hidden';
  let dialogCheck = 0, scanTimer = 0;
  function checkDialogs() {
    dialogCheck = 0;
    clearTimeout(scanTimer);
    scanTimer = 0;
    if (studioClasses.test(html.className) || studioClasses.test(body.className) || document.fullscreenElement) { set('dialog', true); return; }
    // the selector scan forces a layout: run it once things are quiet (a studio building its DOM changes classes often)
    scanTimer = setTimeout(scanDialogs, 150);
  }
  function scanDialogs() {
    scanTimer = 0;
    set('dialog', [...document.querySelectorAll(openSelector)].some(node => node !== bubble && node.getClientRects().length > 0) ||
      [...document.querySelectorAll(studioOpenSelector)].some(rendered));
  }
  const scheduleDialogs = () => { if (!dialogCheck) dialogCheck = requestAnimationFrame(checkDialogs); };
  const rootWatch = new MutationObserver(scheduleDialogs);
  [html, body].forEach(node => rootWatch.observe(node, { attributes: true, attributeFilter: ['class'] }));
  ['click', 'keydown', 'close', 'cancel', 'toggle'].forEach(type => document.addEventListener(type, () => setTimeout(scheduleDialogs, 0), true));
  document.addEventListener('fullscreenchange', scheduleDialogs);
  document.addEventListener('klik:cart-added', scheduleDialogs);

  /* typing on phones */
  const typingField = 'input:not([type=checkbox]):not([type=radio]):not([type=button]):not([type=submit]):not([type=range]),textarea,select,[contenteditable=true]';
  document.addEventListener('focusin', event => set('typing', phone.matches && !!event.target.closest?.(typingField)));
  document.addEventListener('focusout', () => setTimeout(() => set('typing', phone.matches && !!document.activeElement?.closest?.(typingField)), 0));

  /* home hero */
  const heroActions = document.querySelector('.pm-hero .pm-hero-actions');
  if (heroActions && 'IntersectionObserver' in window) {
    new IntersectionObserver(entries => {
      const entry = entries[entries.length - 1];
      set('hero', entry.isIntersecting);
    }).observe(heroActions);
  }

  /* footer: from the moment its top edge enters the viewport */
  const footer = document.querySelector('footer.kf7');
  if (footer && 'IntersectionObserver' in window) {
    new IntersectionObserver(entries => {
      const entry = entries[entries.length - 1];
      set('footer', entry.isIntersecting);
    }).observe(footer);
  }

  /* collide: prices and calls to action under the bubble */
  const targets = [
    '.kprice', '.klik-riyal-price', '.kc4-price', '.kc4-card-kind', '.kc4-card-fact', '.kc4-price-note', '.kc4-card-twin', '.kc4-save', '.price', '[data-klik-price]', '.kpdp-bar', '.kflow-actions',
    '.pm-btn', '.pg-btn', '.pg-option', '.kc4-more-button', '.kc4-filter-toggle', '.cart__checkout', '.kp-button',
    '.kt-button', '.kh-button', '.khx-button', '.kcd-primary', '.kdi-button', '.pm-tabs', '#MainContent button[type=submit]'
  ].join(',');
  const skip = '.kt-header-root,.kf7,.klik-whatsapp-region,dialog,[hidden]';
  const touching = new Set();
  let observed = new WeakSet(), rescanTimer = 0, lastTouch = 0, lastScroll = 0, releaseTimer = 0;
  // Hysteresis: hide at once; release only after 700 ms with nothing under the bubble and 250 ms without scrolling.
  function release() {
    releaseTimer = 0;
    if (touching.size) return;
    const wait = Math.max(700 - (now() - lastTouch), 250 - (now() - lastScroll));
    if (wait > 0) { releaseTimer = setTimeout(release, wait + 16); return; }
    set('collide', false);
  }
  function collide(on) {
    if (on) { lastTouch = now(); clearTimeout(releaseTimer); releaseTimer = 0; set('collide', true); }
    else if (reasons.has('collide') && !releaseTimer) releaseTimer = setTimeout(release, 0);
  }
  function observeAll() {
    rescanTimer = 0;
    if (!collideObserver) return;
    document.querySelectorAll(targets).forEach(node => {
      if (observed.has(node) || node.closest(skip)) return;
      observed.add(node);
      collideObserver.observe(node);
    });
  }
  function buildCollide() {
    collideObserver?.disconnect();
    collideObserver = null;
    observed = new WeakSet();
    touching.clear();
    clearTimeout(releaseTimer);
    releaseTimer = 0;
    set('collide', false);
    if (!('IntersectionObserver' in window) || bubble.hidden || reasons.has('tucked')) return;
    const rect = bubble.getBoundingClientRect();   // one read per resize, never while scrolling
    if (!rect.width) return;
    const pad = 8;
    const top = Math.max(0, rect.top - pad), left = Math.max(0, rect.left - pad);
    const right = Math.max(0, innerWidth - rect.right - pad), bottom = Math.max(0, innerHeight - rect.bottom - pad);
    collideObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        const r = entry.intersectionRect;
        if (entry.isIntersecting && r.width > 1 && r.height > 1) touching.add(entry.target);
        else touching.delete(entry.target);
      });
      if (touching.size) lastTouch = now();
      collide(touching.size > 0);
    }, { rootMargin: `${-top}px ${-right}px ${-bottom}px ${-left}px`, threshold: 0 });
    observeAll();
  }
  function rebuild() { clearTimeout(rebuildTimer); rebuildTimer = setTimeout(buildCollide, 180); }
  addEventListener('resize', rebuild, { passive: true });
  addEventListener('load', rebuild, { once: true });
  document.addEventListener('shopify:section:load', rebuild);
  // klik-product-dock.js toggles `hidden` on the bubble; measure again when it comes back.
  new MutationObserver(rebuild).observe(bubble, { attributes: true, attributeFilter: ['hidden'] });

  /* tucked: phones, after a sustained scroll down */
  let lastY = scrollY, idle = 0, ticking = false, down = 0, up = 0;
  addEventListener('scroll', () => {
    lastScroll = now();
    if (!rescanTimer) rescanTimer = setTimeout(observeAll, 400);
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      ticking = false;
      const y = scrollY, dy = y - lastY;
      if (phone.matches) {
        if (dy > 0) { down += dy; up = 0; } else if (dy < 0) { up -= dy; down = 0; }
        if (down > 120 && y > 240) set('tucked', true);
        else if (up > 40) set('tucked', false);
        clearTimeout(idle);
        idle = setTimeout(() => { down = 0; up = 0; set('tucked', false); }, 700);
      }
      lastY = y;
    });
  }, { passive: true });

  document.addEventListener('klik:dock-blocking', scheduleDialogs);
  addEventListener('pageshow', () => { set('page', pageBlocked()); scheduleDialogs(); });
  checkDialogs();
  apply();
  if (document.readyState === 'complete') rebuild();
})();
